// The one Schedules page (docs/tasks/schedules-unified.md §9,
// routes/schedules_unified.py): every row of the workspace's one schedules
// store, the platform jobs still on gateway cron (read-only), the ids each
// agent may report to, and the writes (make, change, pause, resume, run
// now, approve, delete, a reminder's resend). On a workspace that is not
// switched the list answers `unified: false` (the dashboard then shows the
// old pages) and every write is refused 409 `not_unified`; while the
// rollout switches it, 503 `switching`. Refusals are `{detail: {code,
// message}}` (taskErrorText reads them). Every write refreshes the list,
// the overview, the agents' and subagents' schedule lists and Tasks (a
// Run now gives a task). Types: generated.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { scheduleOverviewKey } from "./scheduleOverview";

type Schemas = components["schemas"];
export type UnifiedSchedule = Schemas["UnifiedScheduleOut"];
export type UnifiedScheduleList = Schemas["UnifiedScheduleList"];
export type PlatformRoutine = Schemas["PlatformRoutineOut"];
export type ScheduleBody = Schemas["ScheduleIn"];
export type SchedulePatchBody = Schemas["SchedulePatchIn"];
export type ScheduleWhen = Schemas["When"];
export type ScheduleTarget = Schemas["Target"];
export type ScheduleWhat = Schemas["What"];
export type ScheduleReport = Schemas["Report"];
export type ScheduleRun = Schemas["ScheduleRunOut"];
export type ScheduleRuns = Schemas["ScheduleRunsOut"];
export type ScheduleFire = Schemas["ScheduleFireOut"];
export type ScheduleAction = "pause" | "resume" | "run" | "approve";

const root = (c: string) => `/api/customers/${encodeURIComponent(c)}/schedules`;
const one = (c: string, id: string) => `${root(c)}/${encodeURIComponent(id)}`;

export const unifiedScheduleKeys = {
  list: (c: string | null) => ["schedules-unified", c] as const,
  runs: (c: string | null, id: string | null) => ["schedules-unified", c, "runs", id] as const,
};

/**
 * The whole page: rows, platform jobs, recipients per agent, limits; `unified` says which page to show.
 * `quiet`: a page that only reads a few rows (a workflow's chains in its editor): no polling; every write here refreshes it.
 */
export function useUnifiedSchedules(customerId: string | null, { quiet = false }: { quiet?: boolean } = {}) {
  return useQuery({
    queryKey: unifiedScheduleKeys.list(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<UnifiedScheduleList>(`${root(customerId!)}/unified`, { signal }),
    staleTime: quiet ? 60_000 : 5_000,
    refetchInterval: quiet ? false : 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Whether the workspace runs on the one schedules store (state.json
 * `schedules_unified`, read from the list route: the flag is not in the
 * settings contract). A quiet observer of the same query: one read per
 * 5 min where nothing else is watching. `known` is false until the first
 * read lands (and after a failed one): show nothing switched-only until then.
 */
export function useSchedulesUnified(customerId: string | null) {
  const q = useQuery({
    queryKey: unifiedScheduleKeys.list(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<UnifiedScheduleList>(`${root(customerId!)}/unified`, { signal }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  return { unified: q.data?.unified === true, known: q.data !== undefined, isLoading: q.isLoading };
}

/** What a row gave or sent, newest first (task runs, or a reminder's notes). */
export function useScheduleRuns(customerId: string | null, id: string | null) {
  return useQuery({
    queryKey: unifiedScheduleKeys.runs(customerId, id),
    enabled: !!customerId && !!id,
    queryFn: ({ signal }) => apiFetch<ScheduleRuns>(`${one(customerId!, id!)}/runs`, { signal }),
    staleTime: 5_000,
    refetchInterval: 30_000,
  });
}

function invalidate(qc: QueryClient, customerId: string) {
  void qc.invalidateQueries({ queryKey: ["schedules-unified", customerId] });
  void qc.invalidateQueries({ queryKey: scheduleOverviewKey(customerId) });
  void qc.invalidateQueries({ queryKey: ["agent-schedules", customerId] });
  void qc.invalidateQueries({ queryKey: ["customer-schedules", customerId] });
  // a subagent's "Runs automatically" shows a new row at once; a Run now is on Tasks
  void qc.invalidateQueries({ queryKey: ["subagent-schedules", customerId] });
  void qc.invalidateQueries({ queryKey: ["tasks", customerId] });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, body }: { customerId: string; body: ScheduleBody }) =>
      apiFetch<UnifiedSchedule>(`${root(customerId)}/unified`, { method: "POST", body }),
    onSettled: (_d, _e, vars) => invalidate(qc, vars.customerId),
  });
}

/** A change; a field left out stays as it is. */
export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id, patch }: { customerId: string; id: string; patch: SchedulePatchBody }) =>
      apiFetch<UnifiedSchedule>(one(customerId, id), { method: "PATCH", body: patch }),
    onSettled: (_d, _e, vars) => invalidate(qc, vars.customerId),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id }: { customerId: string; id: string }) => apiFetch<void>(one(customerId, id), { method: "DELETE" }),
    onSettled: (_d, _e, vars) => invalidate(qc, vars.customerId),
  });
}

/** Pause, resume, run now (outside its timetable) or approve (an agent's team / workflow row). */
export function useScheduleAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id, action }: { customerId: string; id: string; action: ScheduleAction }) =>
      apiFetch<ScheduleFire>(`${one(customerId, id)}/${action}`, { method: "POST" }),
    onSettled: (_d, _e, vars) => invalidate(qc, vars.customerId),
  });
}

/** Send a reminder that failed (or may not have arrived) again. */
export function useResendScheduleNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id, noteId }: { customerId: string; id: string; noteId: string }) =>
      apiFetch<ScheduleFire>(`${one(customerId, id)}/notes/${encodeURIComponent(noteId)}/resend`, { method: "POST" }),
    onSettled: (_d, _e, vars) => invalidate(qc, vars.customerId),
  });
}
